import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import fullscreenBlockStyles from './agwa-fullscreen-block.styles.css?inline';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import {
  claimTakeover,
  releaseTakeover,
  type FullscreenTakeoverHolder,
} from '../../utility/fullscreen-takeover';

/**
 * `<agwa-fullscreen-block>` - lets the block it wraps take the whole viewport, in place.
 *
 * WHY (D-106, a trial the architect asked for after the Azure DevOps work-item pattern): with
 * width already bounded per block, a table takes the window's width on a wide monitor but not
 * its height - the app header, the trail, the tab bar and the page's own header leave a
 * fifty-row table fifteen rows of room on a laptop. Expanded, this wrapper positions ITSELF
 * over the viewport and the block inside keeps its own fill-height chain, so the table gets
 * the viewport's height and width.
 *
 * NOTHING MOVES IN THE DOM. A dialog would have to re-parent the block, which re-runs its
 * connection, re-subscribes its live updates and can re-fetch, for no gain over positioning
 * it: only the host's positioning changes, so ticks, search text, scroll position and SSE
 * subscriptions are exactly what they were.
 *
 * TWO SLOTS, because the expander has to land on the right of the sentence line rather than
 * on the header band's create controls: whatever is slotted as `header` (the scope row and
 * the page's create controls) stays above the block and travels into the takeover with it,
 * and the default slot is the block itself - the rail, the sentence and the table - at whose
 * top-right corner the expander sits.
 *
 * The state is deliberately NOT remembered across reloads: a page that opened taken over
 * would hide the trail and the tabs from someone who had forgotten they left it that way.
 *
 * EVERY BLOCK KEEPS ITS CONTROL; AT MOST ONE IS EXPANDED (#373, architect 2026-09-19: "they
 * should both keep them, and only one can be applied at a time"). A page's tab group and a list
 * inside one of its tabs both offer the mode. Expanding the second CANCELS the first and takes
 * over - last one wins - so the impossible thing is two takeovers being ACTIVE at once rather
 * than two controls existing.
 *
 * THE RULE IS NOT THIS COMPONENT'S. `utility/fullscreen-takeover` holds the one takeover for the
 * whole document, and `agwa-lit-table`'s own full-screen mode claims through the same module, so
 * a table taking the viewport cancels an expanded block and the other way round. This class keeps
 * only its own presentation; the holder, the document lock, Escape and the hidden-tab watcher all
 * live there. The architect stated it as a rule for ANY full screen (2026-09-19: "Stacking non
 * allow. One full screen removes a previous"), and a rule about the document cannot be enforced
 * from inside one component.
 *
 * A CANCELLED TAKEOVER DOES NOT COME BACK (coordinator's decision, 2026-09-19, made so the build
 * was not blocked on it): Escape from the list returns to the ordinary page, not to the tab's
 * takeover. "Cancel" is read as cancelled, and a stack that pops back one level is a surprise
 * nobody asked for. Reversing this is one line - have `yieldTakeover` remember the displaced
 * block and re-expand it on collapse - so it is stated here rather than buried.
 *
 * A TAKEOVER LASTS ONLY WHILE THE BLOCK IS ON SCREEN. It is given up when the host is removed
 * (a route change) and when the block stops being rendered while still connected (its tab panel
 * hidden by a navigation). `disconnectedCallback` carries the first; the registry's own hiding
 * watcher carries the second, which is not a disconnect and which no host should have to
 * remember.
 *
 * Emits `fullscreen-change` - `{ expanded }`, on every change, including when it is displaced.
 * Nothing in the product listens to it yet; see `announceChange`.
 */
@customElement('agwa-fullscreen-block')
export class AgwaFullscreenBlock
  extends LitElement
  implements FullscreenTakeoverHolder
{
  static styles = unsafeCSS(fullscreenBlockStyles);

  /**
   * Reflected, so the takeover's own styling and any journey can read the state off the host.
   */
  @property({ type: Boolean, reflect: true }) expanded = false;

  /**
   * What the bar names while the app header is covered. It overrides `HTMLElement.title`
   * deliberately and is bound as a PROPERTY by every host (`.title=${...}`), never as an
   * attribute: a `title` attribute would put a native tooltip on the whole block, so hovering
   * a row would raise "Products" over the table. Empty falls back to the route's own title,
   * which the router writes to `document.title` verbatim (app-tools router/index.js:230).
   */
  @property({ type: String }) override title = '';

  @query('.expand') private expandControl?: HTMLButtonElement;

  @query('.exit') private exitControl?: HTMLButtonElement;

  override disconnectedCallback() {
    // A route change removes the host mid-takeover; the listener and the document's overflow
    // are global state and would otherwise outlive it. The block's OWN state goes with them:
    // leaving `expanded` true meant a host that MOVED this element rather than destroying it got
    // it back as a full-viewport overlay while the registry believed nobody held the takeover -
    // no lock, no Escape, and the next block to expand claimed successfully, making two.
    //
    // No `fullscreen-change` here: the event exists for a host that is still on screen, and a
    // disconnecting block's host is leaving with it.
    releaseTakeover(this);
    this.expanded = false;
    super.disconnectedCallback();
  }

  /** Takes the viewport, and moves focus to the way out so Tab and Escape both start there. */
  private async expand() {
    if (this.expanded) return;
    // Claiming is what cancels whoever held it - a block, or a table's own full-screen mode.
    claimTakeover(this);
    this.expanded = true;
    this.announceChange();
    await this.updateComplete;
    this.exitControl?.focus();
  }

  /** Returns the block to the page, and focus to the control that expanded it. */
  private async collapse() {
    if (!this.expanded) return;
    releaseTakeover(this);
    this.expanded = false;
    this.announceChange();
    await this.updateComplete;
    this.expandControl?.focus();
  }

  /**
   * The registry's way out: Escape, and a tab panel that stopped being rendered. The await inside
   * `collapse` only sequences the focus return, so nothing here waits on it.
   */
  collapseTakeover(): void {
    void this.collapse();
  }

  /**
   * Cancelled by another holder claiming the takeover. It returns to the page exactly as a
   * collapse does, but touches NO global state: whoever displaced it owns the lock and the
   * listener now, and releasing them here is precisely the two-holders-fighting bug.
   */
  yieldTakeover(): void {
    this.expanded = false;
    this.announceChange();
  }

  /**
   * Announces `fullscreen-change` on every change of this block's state, including when another
   * block cancels it.
   *
   * NOTHING IN THE PRODUCT LISTENS TODAY - this component's own spec is the only subscriber. It
   * is kept because a host that has to react to the mode is a reasonable thing to build and the
   * event is the seam for it; what it must not do is claim a consumer it does not have, which is
   * how the earlier "a tab that re-measures, say" survived a review. The tab re-measure it named
   * is done by the table's own ResizeObserver, not by anything listening here.
   */
  private announceChange() {
    this.dispatchEvent(
      new CustomEvent<{ expanded: boolean }>('fullscreen-change', {
        detail: { expanded: this.expanded },
        bubbles: true,
        composed: true,
      })
    );
  }

  /** The bar's name for the page: the host's title, or the route's if it gave none. */
  private get barTitle(): string {
    return this.title || document.title;
  }

  render() {
    return html`
      ${
        this.expanded
          ? html`
              <div class="bar">
                <span class="bar-title">${this.barTitle}</span>
                <button
                  type="button"
                  class="exit"
                  aria-label="Exit full screen"
                  @click=${() => this.collapse()}
                >
                  <sl-icon name="fullscreen-exit"></sl-icon>
                </button>
              </div>
            `
          : null
      }
      <slot name="header"></slot>
      <div class="content">
        <slot></slot>
        <button
          type="button"
          class="expand"
          aria-label="Expand to full screen"
          aria-expanded=${this.expanded ? 'true' : 'false'}
          @click=${() => this.expand()}
        >
          <sl-icon name="arrows-fullscreen"></sl-icon>
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-fullscreen-block': AgwaFullscreenBlock;
  }
}
